import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { Check, ChevronRight, ChevronLeft, X } from "lucide-react";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const GOLD = "#b8977e";
const GOLD_DARK = "#a07d63";
const GOLD_LIGHT_BG = "#fff9f7";
const BORDER = "#f0e6dc";
const INPUT_BORDER = "#e8d5c4";

const inputStyle = {
  width: "100%",
  padding: "0.65rem 0.9rem",
  borderRadius: "0.75rem",
  border: `1.5px solid ${INPUT_BORDER}`,
  background: "#fff",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.92rem",
  color: "#1a1a1a",
  outline: "none",
  transition: "border-color .2s ease, box-shadow .2s ease",
};

const labelStyle = {
  display: "block",
  fontFamily: "'DM Sans',sans-serif",
  fontSize: "0.78rem",
  fontWeight: 600,
  color: "#555",
  marginBottom: "0.35rem",
  letterSpacing: "0.02em",
};

const onInputFocus = (e) => {
  e.target.style.borderColor = GOLD;
  e.target.style.boxShadow = "0 0 0 3px rgba(184,151,126,0.15)";
};
const onInputBlur = (e) => {
  e.target.style.borderColor = INPUT_BORDER;
  e.target.style.boxShadow = "none";
};

const OPTIONS_CATALOG = [
  { id: "magnet_50", label: "Support Photo Aimanté (50 unités)", price: 50 },
  { id: "magnet_100", label: "Support Photo Aimanté (100 unités)", price: 80 },
  { id: "backdrop", label: "Fond pour Photos", price: 40 },
];

const EVENT_TYPES = ["Mariage", "Anniversaire", "Baptême", "Corporate", "Soirée", "Autre"];

const StepIndicator = ({ currentStep }) => {
  const steps = [
    { num: 1, label: "Formule" },
    { num: 2, label: "Forfait & Date" },
    { num: 3, label: "Client" },
    { num: 4, label: "Paiement" },
  ];
  return (
    <div style={{ padding: "0.5rem 0 1rem", borderBottom: `1px solid ${BORDER}` }} data-testid="admin-step-indicator">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.25rem" }}>
        {steps.map((s, i) => {
          const isPast = currentStep > s.num;
          const isActive = currentStep === s.num;
          return (
            <div key={s.num} style={{ display: "flex", alignItems: "center" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
                <div
                  style={{
                    width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                    background: isPast || isActive ? GOLD : "#fff",
                    border: isPast || isActive ? `2px solid ${GOLD}` : "2px solid #ddd",
                    transition: "all 0.4s ease",
                  }}
                >
                  {isPast ? (
                    <Check style={{ width: 14, height: 14, color: "#fff", strokeWidth: 3 }} />
                  ) : (
                    <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.78rem", color: isActive ? "#fff" : "#bbb" }}>{s.num}</span>
                  )}
                </div>
                <span
                  style={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem",
                    fontWeight: isActive ? 600 : 400,
                    color: isActive ? GOLD : isPast ? "#777" : "#bbb",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.label}
                </span>
              </div>
              {i < steps.length - 1 && (
                <div style={{ width: 36, height: 2, margin: "0 0.4rem", marginBottom: "1.1rem", background: currentStep > s.num ? GOLD : "#ddd", transition: "background 0.4s ease" }} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PricingTypeCard = ({ id, icon, title, tagline, from, testid, onClick }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onClick(id)}
    style={{
      background: "#fff",
      border: `1.5px solid ${BORDER}`,
      borderRadius: "1.5rem",
      padding: "2rem 1.75rem",
      textAlign: "left",
      cursor: "pointer",
      transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
      fontFamily: "'DM Sans',sans-serif",
      boxShadow: "0 2px 14px rgba(0,0,0,0.04)",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-6px)";
      e.currentTarget.style.boxShadow = "0 16px 40px rgba(184,151,126,0.22)";
      e.currentTarget.style.borderColor = GOLD;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 2px 14px rgba(0,0,0,0.04)";
      e.currentTarget.style.borderColor = BORDER;
    }}
  >
    <div style={{ fontSize: "2.6rem", marginBottom: "0.5rem", lineHeight: 1 }}>{icon}</div>
    <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.7rem", fontWeight: 700, color: "#1a1a1a", letterSpacing: "0.02em", marginBottom: "0.5rem" }}>{title}</h3>
    <p style={{ color: "#7a6a5e", fontSize: "0.9rem", marginBottom: "1rem", lineHeight: 1.5 }}>{tagline}</p>
    <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.25rem", fontWeight: 700, color: GOLD, marginBottom: "0.5rem" }}>À partir de {from}€</p>
    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", color: GOLD, fontWeight: 600, fontSize: "0.9rem" }}>
      Choisir cette formule <ChevronRight style={{ width: 14, height: 14 }} />
    </span>
  </button>
);

const Recap = ({ pricingType, selectedPackage, packages, formData, options, totals, workflowStatus, promoCode, paymentMethod }) => {
  const pkg = selectedPackage ? packages[selectedPackage] : null;
  const wsLabel = ({
    draft: "📋 Brouillon",
    quote_sent: "📨 Devis envoyé",
    confirmed: "✅ Confirmé",
  })[workflowStatus] || workflowStatus;

  return (
    <aside data-testid="admin-recap-sticky" style={{ background: "#faf6f2", border: `1px solid ${BORDER}`, borderRadius: "1.25rem", padding: "1.5rem", fontFamily: "'DM Sans',sans-serif", position: "sticky", top: 0 }}>
      <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: 600, letterSpacing: "0.12em", color: GOLD, textTransform: "uppercase", marginBottom: "0.25rem" }}>Récapitulatif</p>
      <div style={{ width: 32, height: 2, background: GOLD, marginBottom: "1rem" }} />

      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "1rem" }}>
        {pricingType && (
          <span data-testid="recap-pricing-badge" style={{ background: GOLD_LIGHT_BG, color: GOLD, border: `1px solid ${BORDER}`, padding: "0.2rem 0.7rem", borderRadius: 50, fontSize: "0.72rem", fontWeight: 600 }}>
            {pricingType === "prints" ? "📸 Par impressions" : "🎉 Illimité"}
          </span>
        )}
        {workflowStatus && (
          <span data-testid="recap-workflow-badge" style={{ background: "#fff", color: "#555", border: `1px solid ${BORDER}`, padding: "0.2rem 0.7rem", borderRadius: 50, fontSize: "0.72rem", fontWeight: 600 }}>
            {wsLabel}
          </span>
        )}
      </div>

      {pkg ? (
        <>
          <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.25rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.15rem" }}>{pkg.name}</p>
          <p style={{ fontSize: "0.82rem", color: "#777", marginBottom: "1rem" }}>
            {pkg.max_prints ? `${pkg.max_prints} impressions · ` : ""}{pkg.duration}
          </p>
        </>
      ) : (
        <p style={{ fontSize: "0.85rem", color: "#aaa", marginBottom: "1rem" }}>Sélectionnez un forfait</p>
      )}

      {formData.event_date && (
        <p style={{ fontSize: "0.85rem", color: "#444", marginBottom: "0.35rem" }}>📅 {formData.event_date}{formData.event_start_time ? ` · ${formData.event_start_time}` : ""}</p>
      )}
      {formData.event_location && (
        <p style={{ fontSize: "0.85rem", color: "#444", marginBottom: "0.35rem" }}>📍 {formData.event_location}</p>
      )}
      {formData.event_type && (
        <p style={{ fontSize: "0.85rem", color: "#444", marginBottom: "0.75rem" }}>{formData.event_type}</p>
      )}

      {formData.customer_name && (
        <>
          <div style={{ borderTop: `1px solid ${BORDER}`, margin: "0.9rem 0", paddingTop: "0.75rem" }}>
            <p style={{ fontSize: "0.78rem", color: "#999", marginBottom: "0.25rem" }}>Client</p>
            <p style={{ fontSize: "0.9rem", color: "#1a1a1a", fontWeight: 600 }}>{formData.customer_name}</p>
            {formData.customer_email && <p style={{ fontSize: "0.78rem", color: "#666" }}>📧 {formData.customer_email}</p>}
            {formData.customer_phone && <p style={{ fontSize: "0.78rem", color: "#666" }}>📞 {formData.customer_phone}</p>}
          </div>
        </>
      )}

      {Object.values(options).some(Boolean) && (
        <div style={{ borderTop: `1px solid ${BORDER}`, margin: "0.9rem 0", paddingTop: "0.75rem" }}>
          <p style={{ fontSize: "0.78rem", color: "#999", marginBottom: "0.35rem" }}>Options</p>
          {OPTIONS_CATALOG.filter(o => options[o.id]).map(o => (
            <p key={o.id} style={{ fontSize: "0.82rem", color: "#444", display: "flex", justifyContent: "space-between" }}>
              <span>+ {o.label}</span><span style={{ fontWeight: 600 }}>+{o.price}€</span>
            </p>
          ))}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${BORDER}`, margin: "0.9rem 0", paddingTop: "0.75rem", fontSize: "0.88rem" }}>
        <p style={{ display: "flex", justifyContent: "space-between", color: "#555", marginBottom: "0.25rem" }}>
          <span>Sous-total</span><span>{totals.subtotal.toFixed(2)}€</span>
        </p>
        {totals.discount > 0 && (
          <p style={{ display: "flex", justifyContent: "space-between", color: "#2d7a4f", marginBottom: "0.25rem" }}>
            <span>Code {promoCode}</span><span>-{totals.discount.toFixed(2)}€</span>
          </p>
        )}
        <p data-testid="recap-total" style={{ display: "flex", justifyContent: "space-between", fontFamily: "'Playfair Display',serif", fontSize: "1.4rem", fontWeight: 700, color: GOLD, marginTop: "0.4rem" }}>
          <span>TOTAL</span><span>{totals.total.toFixed(2)}€</span>
        </p>
      </div>

      {paymentMethod && (
        <p style={{ fontSize: "0.78rem", color: "#777", marginTop: "0.75rem", textAlign: "center" }}>
          💳 {({ stripe: "Stripe", whatsapp: "WhatsApp", transfer: "Virement", cash: "Espèces", devis: "Devis" }[paymentMethod]) || paymentMethod}
        </p>
      )}
    </aside>
  );
};

export const AddBookingDialog = ({ open, onClose, onSave }) => {
  const [packages, setPackages] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [pricingType, setPricingType] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [formData, setFormData] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    event_location: "", event_type: "", event_date: "", event_start_time: "",
    delivery_zone: "idf",
    description: "",
  });
  const [options, setOptions] = useState({ magnet_50: false, magnet_100: false, backdrop: false });
  const [paymentMethod, setPaymentMethod] = useState("whatsapp");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [depositAmount, setDepositAmount] = useState("");
  const [workflowStatus, setWorkflowStatus] = useState("draft");
  const [promoCode, setPromoCode] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Fetch packages on open — use admin endpoint to bypass client-side filter
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/admin/packages`).then(r => setPackages(r.data?.packages || {})).catch(() => setPackages({}));
  }, [open]);

  // Reset when modal closes
  useEffect(() => {
    if (open) return;
    const t = setTimeout(() => {
      setCurrentStep(1);
      setPricingType(null);
      setSelectedPackage("");
      setFormData({ customer_name: "", customer_email: "", customer_phone: "", event_location: "", event_type: "", event_date: "", event_start_time: "", delivery_zone: "idf", description: "" });
      setOptions({ magnet_50: false, magnet_100: false, backdrop: false });
      setPaymentMethod("whatsapp");
      setPaymentStatus("pending");
      setDepositAmount("");
      setWorkflowStatus("draft");
      setPromoCode("");
      setAdminNotes("");
    }, 280);
    return () => clearTimeout(t);
  }, [open]);

  const filteredPackages = useMemo(() => {
    return Object.entries(packages).filter(([_, pkg]) => (pkg.pricing_type || "unlimited") === pricingType);
  }, [packages, pricingType]);

  const totals = useMemo(() => {
    const pkg = selectedPackage ? packages[selectedPackage] : null;
    const pkgPrice = pkg?.price || 0;
    const optionsTotal = OPTIONS_CATALOG.filter(o => options[o.id]).reduce((s, o) => s + o.price, 0);
    const deliverySurcharge = formData.delivery_zone === "frontalier" ? 30 : 0;
    const subtotal = pkgPrice + optionsTotal + deliverySurcharge;
    // Promo: only flat-percentage placeholder for UI parity (real promo applied server-side via promotion payload).
    let discount = 0;
    if (promoCode && pkgPrice > 0) {
      if (promoCode.toUpperCase().startsWith("HACK")) discount = Math.round(pkgPrice * 0.10 * 100) / 100;
    }
    return { subtotal, discount, total: Math.max(0, subtotal - discount) };
  }, [packages, selectedPackage, options, formData.delivery_zone, promoCode]);

  const canStep2 = !!pricingType;
  const canStep3 = !!selectedPackage && !!formData.event_date && !!formData.event_location;
  const canStep4 = !!formData.customer_name && !!formData.customer_email && !!formData.customer_phone;

  const goNext = () => setCurrentStep((s) => Math.min(4, s + 1));
  const goPrev = () => setCurrentStep((s) => Math.max(1, s - 1));

  const handleSubmit = async () => {
    if (!selectedPackage || !formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.event_date || !formData.event_location) {
      toast.error("Veuillez compléter toutes les étapes avant de soumettre");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        package_id: selectedPackage,
        event_date: formData.event_date,
        event_start_time: formData.event_start_time || null,
        options,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        event_type: formData.event_type || null,
        event_location: formData.event_location,
        delivery_zone: formData.delivery_zone,
        payment_method: paymentMethod,
        payment_status: paymentStatus,
        workflow_status: workflowStatus,
        admin_notes: adminNotes || null,
        pricing_type: pricingType,
      };
      if (onSave) {
        // Let parent control endpoint + refresh
        await onSave(payload);
      } else {
        await axios.post(`${API}/admin/bookings`, payload);
        toast.success("Réservation créée");
        onClose && onClose();
      }
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || "Erreur lors de la création";
      toast.error(typeof detail === "string" ? detail : "Erreur lors de la création");
    } finally {
      setSubmitting(false);
    }
  };

  const submitLabel = ({
    draft: "Enregistrer en brouillon",
    quote_sent: "Créer + Envoyer le devis",
    confirmed: "Créer la Réservation",
  })[workflowStatus] || "Créer la Réservation";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="p-0 border-0"
        style={{ maxWidth: 1200, width: "95vw", maxHeight: "92vh", overflow: "hidden", background: "#fff", borderRadius: "1.25rem" }}
      >
        {/* Header */}
        <div style={{ padding: "1.5rem 2rem 0.75rem", borderBottom: `1px solid ${BORDER}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
          <div>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.65rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.2rem" }}>
              Nouvelle réservation manuelle
            </h2>
            <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.85rem", color: "#7a6a5e" }}>
              Source : <strong>Admin</strong> · même flux qu'une réservation client
            </p>
          </div>
          <button
            type="button"
            data-testid="admin-add-booking-close"
            onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#999", padding: "0.25rem" }}
            aria-label="Fermer"
          >
            <X style={{ width: 22, height: 22 }} />
          </button>
        </div>

        <div style={{ padding: "0 2rem" }}>
          <StepIndicator currentStep={currentStep} />
        </div>

        {/* Body — grid: content + sticky recap */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) 320px",
            gap: "2rem",
            padding: "1.5rem 2rem 1rem",
            maxHeight: "calc(92vh - 220px)",
            overflowY: "auto",
          }}
          className="admin-add-booking-body"
        >
          <div data-testid={`admin-step-content-${currentStep}`}>
            {/* STEP 1 — pricing type */}
            {currentStep === 1 && (
              <div>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.35rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.25rem" }}>
                  Quel format pour ce client ?
                </h3>
                <p style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: "italic", color: "#7a6a5e", marginBottom: "1.5rem" }}>
                  Choisissez la formule pour commencer
                </p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "1.25rem" }}>
                  <PricingTypeCard
                    id="unlimited"
                    icon="🎉"
                    title="ILLIMITÉ"
                    tagline="Impressions illimitées pendant la durée choisie"
                    from={270}
                    testid="admin-select-unlimited"
                    onClick={(t) => { setPricingType(t); setSelectedPackage(""); goNext(); }}
                  />
                  <PricingTypeCard
                    id="prints"
                    icon="📸"
                    title="PAR IMPRESSIONS"
                    tagline="Nombre de tirages défini avec durée incluse"
                    from={190}
                    testid="admin-select-prints"
                    onClick={(t) => { setPricingType(t); setSelectedPackage(""); goNext(); }}
                  />
                </div>
              </div>
            )}

            {/* STEP 2 — forfait + date + lieu */}
            {currentStep === 2 && (
              <div>
                <button
                  type="button"
                  data-testid="admin-back-to-pricing"
                  onClick={() => { setPricingType(null); setSelectedPackage(""); setCurrentStep(1); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", background: "transparent", border: "none", color: GOLD, fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", fontWeight: 600, cursor: "pointer", marginBottom: "1rem", padding: 0 }}
                >
                  <ChevronLeft style={{ width: 14, height: 14 }} /> Changer de formule
                  <span style={{ marginLeft: "0.35rem", background: GOLD_LIGHT_BG, color: GOLD, fontSize: "0.7rem", padding: "0.15rem 0.55rem", borderRadius: 50, border: `1px solid ${BORDER}` }}>
                    {pricingType === "prints" ? "📸 Par impressions" : "🎉 Illimité"}
                  </span>
                </button>

                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.2rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "0.75rem" }}>Forfait</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.9rem", marginBottom: "1.5rem" }}>
                  {filteredPackages.map(([key, pkg]) => {
                    const sel = selectedPackage === key;
                    const isPrints = (pkg.pricing_type || "unlimited") === "prints";
                    const maxPrints = pkg.max_prints || 0;
                    return (
                      <div
                        key={key}
                        data-testid={`admin-package-card-${key}`}
                        onClick={() => setSelectedPackage(sel ? "" : key)}
                        style={{
                          position: "relative",
                          border: sel ? `2px solid ${GOLD}` : `1.5px solid ${BORDER}`,
                          borderRadius: "1.25rem",
                          padding: "1.2rem 1.1rem",
                          background: sel ? GOLD_LIGHT_BG : "#fff",
                          cursor: "pointer",
                          transition: "all 0.25s ease",
                          boxShadow: sel ? "0 6px 25px rgba(184,151,126,0.2)" : "none",
                        }}
                      >
                        {isPrints && (
                          <div
                            data-testid={`admin-package-prints-badge-${key}`}
                            style={{ position: "absolute", top: "0.65rem", right: "0.65rem", background: "#fff", color: GOLD, border: `1px solid ${BORDER}`, fontSize: "0.68rem", fontWeight: 700, padding: "0.2rem 0.55rem", borderRadius: 50, whiteSpace: "nowrap" }}
                          >
                            📸 {maxPrints}
                          </div>
                        )}
                        <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.1rem", fontWeight: 700, color: "#1a1a1a", marginTop: isPrints ? "1.2rem" : 0 }}>{pkg.name}</p>
                        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 600, color: GOLD, marginBottom: "0.5rem" }}>{pkg.duration}</p>
                        <p style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.5rem", fontWeight: 700, color: GOLD }}>{pkg.price}€</p>
                        {isPrints && maxPrints > 0 && (
                          <div data-testid={`admin-package-gauge-${key}`} style={{ marginTop: "0.5rem" }}>
                            <div style={{ width: "100%", height: 5, background: "#f0e4da", borderRadius: 4, overflow: "hidden" }}>
                              <div style={{ width: `${Math.min(100, (maxPrints / 700) * 100)}%`, height: "100%", background: `linear-gradient(90deg, ${GOLD}, #d4b39c)`, borderRadius: 4 }} />
                            </div>
                            <p style={{ fontSize: "0.68rem", color: "#999", marginTop: "0.2rem" }}>{maxPrints} / 700 impressions</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.9rem" }}>
                  <div>
                    <label style={labelStyle}>Date de l'événement *</label>
                    <input data-testid="admin-input-event-date" type="date" value={formData.event_date} onChange={e => setFormData({ ...formData, event_date: e.target.value })} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div>
                    <label style={labelStyle}>Heure de début</label>
                    <input data-testid="admin-input-event-time" type="time" value={formData.event_start_time} onChange={e => setFormData({ ...formData, event_start_time: e.target.value })} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Lieu *</label>
                    <input data-testid="admin-input-event-location" type="text" value={formData.event_location} onChange={e => setFormData({ ...formData, event_location: e.target.value })} placeholder="Paris, France" style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div>
                    <label style={labelStyle}>Type d'événement</label>
                    <select data-testid="admin-input-event-type" value={formData.event_type} onChange={e => setFormData({ ...formData, event_type: e.target.value })} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur}>
                      <option value="">— Choisir —</option>
                      {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Zone de livraison</label>
                    <select data-testid="admin-input-delivery-zone" value={formData.delivery_zone} onChange={e => setFormData({ ...formData, delivery_zone: e.target.value })} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur}>
                      <option value="idf">Île-de-France (incluse)</option>
                      <option value="frontalier">Zone frontalière (+30€)</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 3 — client */}
            {currentStep === 3 && (
              <div>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.2rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "1rem" }}>Coordonnées du client</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "0.9rem" }}>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Nom complet *</label>
                    <input data-testid="admin-input-customer-name" type="text" value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} placeholder="Jean Dupont" style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div>
                    <label style={labelStyle}>Email *</label>
                    <input data-testid="admin-input-customer-email" type="email" value={formData.customer_email} onChange={e => setFormData({ ...formData, customer_email: e.target.value })} placeholder="jean@exemple.fr" style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div>
                    <label style={labelStyle}>Téléphone *</label>
                    <input data-testid="admin-input-customer-phone" type="tel" value={formData.customer_phone} onChange={e => setFormData({ ...formData, customer_phone: e.target.value })} placeholder="+33 6 12 34 56 78" style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <label style={labelStyle}>Description / Notes visibles au client</label>
                    <textarea data-testid="admin-input-description" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} rows={3} placeholder="Notes visibles dans le devis et l'email…" style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans',sans-serif" }} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                </div>
              </div>
            )}

            {/* STEP 4 — admin config */}
            {currentStep === 4 && (
              <div>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.2rem", fontWeight: 700, color: "#1a1a1a", marginBottom: "1rem" }}>Options & configuration admin</h3>

                <div style={{ marginBottom: "1.25rem" }}>
                  <label style={{ ...labelStyle, marginBottom: "0.5rem" }}>Options additionnelles</label>
                  {OPTIONS_CATALOG.map(o => (
                    <label key={o.id} data-testid={`admin-option-${o.id}`} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.55rem 0.8rem", border: `1.5px solid ${options[o.id] ? GOLD : INPUT_BORDER}`, borderRadius: "0.75rem", marginBottom: "0.4rem", cursor: "pointer", background: options[o.id] ? GOLD_LIGHT_BG : "#fff", transition: "all .2s ease" }}>
                      <input type="checkbox" checked={!!options[o.id]} onChange={e => setOptions({ ...options, [o.id]: e.target.checked })} style={{ accentColor: GOLD, width: 16, height: 16 }} />
                      <span style={{ flex: 1, fontFamily: "'DM Sans',sans-serif", fontSize: "0.88rem", color: "#444" }}>{o.label}</span>
                      <span style={{ fontFamily: "'DM Sans',sans-serif", fontWeight: 700, color: GOLD, fontSize: "0.88rem" }}>+{o.price}€</span>
                    </label>
                  ))}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.9rem", marginBottom: "1.25rem" }}>
                  <div>
                    <label style={labelStyle}>Mode de règlement</label>
                    <select data-testid="admin-input-payment-method" value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur}>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="stripe">Stripe (CB)</option>
                      <option value="transfer">Virement</option>
                      <option value="cash">Espèces</option>
                      <option value="devis">Devis (à valider)</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Statut paiement</label>
                    <select data-testid="admin-input-payment-status" value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur}>
                      <option value="pending">En attente</option>
                      <option value="paid">Payée</option>
                      <option value="partial">Acompte versé</option>
                    </select>
                  </div>
                  {paymentStatus === "partial" && (
                    <div style={{ gridColumn: "1 / -1" }}>
                      <label style={labelStyle}>Montant de l'acompte (€)</label>
                      <input data-testid="admin-input-deposit-amount" type="number" min="0" step="0.01" value={depositAmount} onChange={e => setDepositAmount(e.target.value)} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>Statut workflow</label>
                    <select data-testid="admin-input-workflow-status" value={workflowStatus} onChange={e => setWorkflowStatus(e.target.value)} style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur}>
                      <option value="draft">Brouillon</option>
                      <option value="quote_sent">Devis envoyé</option>
                      <option value="confirmed">Confirmée</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>Code promo (optionnel)</label>
                    <input data-testid="admin-input-promo-code" type="text" value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="HACK10" style={inputStyle} onFocus={onInputFocus} onBlur={onInputBlur} />
                  </div>
                </div>

                <div>
                  <label style={labelStyle}>Notes admin (privées — non visibles client)</label>
                  <textarea data-testid="admin-input-admin-notes" value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3} placeholder="Particularité de la prestation, contact interne…" style={{ ...inputStyle, resize: "vertical", fontFamily: "'DM Sans',sans-serif" }} onFocus={onInputFocus} onBlur={onInputBlur} />
                </div>
              </div>
            )}
          </div>

          {/* Right column: sticky recap (hidden when step 1) */}
          {currentStep > 1 && (
            <Recap
              pricingType={pricingType}
              selectedPackage={selectedPackage}
              packages={packages}
              formData={formData}
              options={options}
              totals={totals}
              workflowStatus={workflowStatus}
              promoCode={promoCode}
              paymentMethod={paymentMethod}
            />
          )}
        </div>

        {/* Footer actions */}
        <div style={{ borderTop: `1px solid ${BORDER}`, padding: "1rem 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem", background: "#fff" }}>
          <button
            type="button"
            data-testid="admin-step-prev"
            onClick={goPrev}
            disabled={currentStep === 1}
            style={{
              padding: "0.7rem 1.5rem", borderRadius: "0.75rem", border: `1.5px solid ${GOLD}`,
              background: "transparent", color: GOLD, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.9rem",
              cursor: currentStep === 1 ? "not-allowed" : "pointer", opacity: currentStep === 1 ? 0.35 : 1, transition: "all .2s ease",
            }}
          >
            ← Précédent
          </button>
          {currentStep < 4 && (
            <button
              type="button"
              data-testid="admin-step-next"
              onClick={goNext}
              disabled={(currentStep === 1 && !canStep2) || (currentStep === 2 && !canStep3) || (currentStep === 3 && !canStep4)}
              style={{
                padding: "0.85rem 2rem", borderRadius: "0.75rem", border: "none",
                background: GOLD, color: "#fff", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.92rem",
                cursor: "pointer", transition: "background .2s ease",
                opacity: ((currentStep === 1 && !canStep2) || (currentStep === 2 && !canStep3) || (currentStep === 3 && !canStep4)) ? 0.4 : 1,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = GOLD_DARK; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = GOLD; }}
            >
              Suivant →
            </button>
          )}
          {currentStep === 4 && (
            <button
              type="button"
              data-testid="admin-submit-booking"
              onClick={handleSubmit}
              disabled={submitting || !canStep3 || !canStep4}
              style={{
                padding: "0.85rem 2rem", borderRadius: "0.75rem", border: "none",
                background: GOLD, color: "#fff", fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.92rem",
                cursor: submitting ? "wait" : "pointer", transition: "background .2s ease",
                opacity: submitting ? 0.6 : 1,
              }}
              onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = GOLD_DARK; }}
              onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = GOLD; }}
            >
              {submitting ? "Enregistrement…" : submitLabel}
            </button>
          )}
        </div>

        {/* Mobile responsive override */}
        <style>{`
          @media (max-width: 900px) {
            .admin-add-booking-body {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default AddBookingDialog;
